import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { MdAdd } from "react-icons/md";
import { RiMoneyDollarCircleLine } from "react-icons/ri";

import HeadlessDialog from "@components/Dialog";
import HeadlessButton from "@components/Button";
import ScrollableModalBody from "@components/ScrollableModalBody";
import Spinner from "@components/Spinner";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import {
  fetchWorkerPayRates,
  updatePayRate,
  createPayRate,
} from "@service/PayRateService";
import {
  fetchWorkerBillingRates,
  updateBillingRate,
  createBillingRate,
} from "@service/BillRateService";
import { IRateDTO, IRateUpsertDTO } from "@interfaces/IRateDTO";

/** Number of days in the past/future within which rates can be created/edited */
const RATE_EDIT_WINDOW_DAYS = 14;

type RateCategory = "pay" | "billing";
type OperationMode = "create" | "edit" | null;

interface ContractorRatesManageModalProps {
  workerId: number;
  contractorId: number;
  contractorName: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const RATE_TYPE_OPTIONS = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

const createInitialForm = (): IRateUpsertDTO => ({
  rate_type: "hourly",
  value: "",
  start_date: "",
  end_date: "",
});

function getMinDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - RATE_EDIT_WINDOW_DAYS);
  return d.toISOString().slice(0, 10);
}

function isDateInEditWindow(dateStr: string): boolean {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const min = new Date(getMinDate());
  min.setHours(0, 0, 0, 0);
  return date >= min;
}

export default function ContractorRatesManageModal({
  workerId,
  contractorId,
  contractorName,
  isOpen,
  onClose,
  onSaved,
}: ContractorRatesManageModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [payRates, setPayRates] = useState<IRateDTO[]>([]);
  const [billingRates, setBillingRates] = useState<IRateDTO[]>([]);
  const [operationMode, setOperationMode] = useState<OperationMode>(null);
  const [rateCategory, setRateCategory] = useState<RateCategory>("pay");
  const [selectedRate, setSelectedRate] = useState<IRateDTO | null>(null);
  const [formValues, setFormValues] = useState<IRateUpsertDTO>(
    createInitialForm()
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const breakpoint = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});
  const handleContextMenu = useCallback((event: MouseEvent) => {
    event.preventDefault();
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadData = useCallback(async () => {
    if (!isOpen || !workerId) return;

    setIsLoading(true);
    setError(null);
    try {
      const [payData, billingData] = await Promise.all([
        fetchWorkerPayRates(workerId),
        fetchWorkerBillingRates(workerId),
      ]);
      setPayRates(Array.isArray(payData) ? payData : []);
      setBillingRates(Array.isArray(billingData) ? billingData : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load rates.");
      setPayRates([]);
      setBillingRates([]);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, workerId]);

  useEffect(() => {
    if (!isOpen) return;
    setOperationMode(null);
    setSelectedRate(null);
    setFormValues(createInitialForm());
    setSuccess(null);
    setError(null);
    loadData();
  }, [isOpen, loadData]);

  const getRowStyle = useCallback((params: any) => {
    if (!params.data?.end_date) {
      return { color: "#2563eb" };
    }
    return undefined;
  }, []);

  const rateColumnDefs = useMemo(
    () => [
      {
        headerName: "Type",
        field: "rate_type",
        filter: true,
        width: 100,
      },
      {
        headerName: "Value",
        field: "value",
        width: 100,
        filter: true,
        valueFormatter: (params: any) =>
          params.value != null ? `$${Number(params.value).toFixed(2)}` : "",
      },
      {
        headerName: "Start Date",
        field: "start_date",
        width: 120,
        filter: true,
      },
      {
        headerName: "End Date",
        field: "end_date",
        width: 120,
        filter: true,
        valueFormatter: (params: any) => params.value || "Current",
      },
    ],
    []
  );

  const isRateEditable = useCallback((rate: IRateDTO): boolean => {
    if (rate.start_date && !isDateInEditWindow(rate.start_date)) return false;
    if (rate.end_date && !isDateInEditWindow(rate.end_date)) return false;
    return true;
  }, []);

  const handlePayRowClicked = useCallback(
    (event: any) => {
      const row = event?.data as IRateDTO;
      if (!row) return;

      if (!isRateEditable(row)) {
        setError(
          `This pay rate cannot be edited because its dates are outside the ${RATE_EDIT_WINDOW_DAYS}-day edit window.`
        );
        setOperationMode(null);
        setSelectedRate(null);
        return;
      }

      setOperationMode("edit");
      setRateCategory("pay");
      setSelectedRate(row);
      setFormValues({
        rate_type: row.rate_type || "hourly",
        value: row.value != null ? String(row.value) : "",
        start_date: row.start_date || "",
        end_date: row.end_date || "",
      });
      setSuccess(null);
      setError(null);
    },
    [isRateEditable]
  );

  const handleBillingRowClicked = useCallback(
    (event: any) => {
      const row = event?.data as IRateDTO;
      if (!row) return;

      if (!isRateEditable(row)) {
        setError(
          `This billing rate cannot be edited because its dates are outside the ${RATE_EDIT_WINDOW_DAYS}-day edit window.`
        );
        setOperationMode(null);
        setSelectedRate(null);
        return;
      }

      setOperationMode("edit");
      setRateCategory("billing");
      setSelectedRate(row);
      setFormValues({
        rate_type: row.rate_type || "hourly",
        value: row.value != null ? String(row.value) : "",
        start_date: row.start_date || "",
        end_date: row.end_date || "",
      });
      setSuccess(null);
      setError(null);
    },
    [isRateEditable]
  );

  const startCreateMode = useCallback((category: RateCategory) => {
    setOperationMode("create");
    setRateCategory(category);
    setSelectedRate(null);
    setFormValues({
      ...createInitialForm(),
      start_date: "",
    });
    setSuccess(null);
    setError(null);
  }, []);

  const clearOperation = useCallback((clearMessages = true) => {
    setOperationMode(null);
    setSelectedRate(null);
    setFormValues(createInitialForm());
    if (clearMessages) {
      setSuccess(null);
      setError(null);
    }
  }, []);

  const handleSave = useCallback(async () => {
    setError(null);
    setSuccess(null);

    if (!operationMode) {
      setError("Select Create New or a rate row before saving.");
      return;
    }

    if (!formValues.rate_type) {
      setError("Rate type is required.");
      return;
    }

    if (!formValues.value || Number(formValues.value) <= 0) {
      setError("A valid rate value is required.");
      return;
    }

    if (!formValues.start_date) {
      setError("Start date is required.");
      return;
    }

    if (!isDateInEditWindow(formValues.start_date)) {
      setError(
        `Start date must be within ${RATE_EDIT_WINDOW_DAYS} days of today.`
      );
      return;
    }

    setIsSaving(true);
    try {
      const label = rateCategory === "pay" ? "Pay rate" : "Billing rate";

      if (operationMode === "edit") {
        if (!selectedRate) {
          setError("No rate selected for editing.");
          return;
        }

        const payload: IRateUpsertDTO = {
          rate_type: formValues.rate_type,
          value: formValues.value,
          start_date: formValues.start_date,
          end_date: formValues.end_date || null,
        };

        if (rateCategory === "pay") {
          await updatePayRate(selectedRate.id, payload);
        } else {
          await updateBillingRate(selectedRate.id, payload);
        }
        setSuccess(`${label} updated successfully.`);
      } else {
        const payload = {
          worker_id: workerId,
          rate_type: formValues.rate_type as "hourly" | "daily" | "weekly",
          value: formValues.value,
          start_date: formValues.start_date,
        };

        if (rateCategory === "pay") {
          await createPayRate(payload);
        } else {
          await createBillingRate(payload);
        }
        setSuccess(`${label} created successfully.`);
      }

      await loadData();
      onSaved();
      clearOperation(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to save rate.");
    } finally {
      setIsSaving(false);
    }
  }, [
    operationMode,
    formValues,
    selectedRate,
    rateCategory,
    workerId,
    loadData,
    onSaved,
    clearOperation,
  ]);

  if (!isOpen) return null;

  const categoryLabel = rateCategory === "pay" ? "Pay Rate" : "Billing Rate";

  return (
    <HeadlessDialog
      title={
        <div className="tw-flex tw-items-center tw-gap-2">
          <RiMoneyDollarCircleLine
            className="tw-text-primary-light"
            size={24}
          />
          <span>{`Rates - ${contractorName} (Contractor ID: ${contractorId})`}</span>
        </div>
      }
      description="Manage pay rates and billing rates for this contractor"
      isOpen={isOpen}
      setIsOpen={(open: boolean) => {
        if (!open) onClose();
      }}
      onClose={onClose}
      buttonText="Close"
      size="tw-w-[100vw] xl:tw-max-w-[75vw]"
    >
      {isLoading ? (
        <div className="tw-p-6">
          <Spinner className="tw-flex tw-justify-center tw-items-center" />
        </div>
      ) : (
        <ScrollableModalBody className="tw-overflow-y-scroll">
          <div className="tw-space-y-2 tw-ml-[2px]">
            {/* Two grids side by side */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              {/* Pay Rates Grid */}
              <div
                className={`tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 ${
                  operationMode && rateCategory === "pay"
                    ? "tw-bg-gray-200"
                    : "tw-bg-white"
                }`}
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                  <h3
                    className={`tw-text-lg tw-font-semibold ${
                      operationMode && rateCategory === "pay"
                        ? "tw-text-blue-700"
                        : "tw-text-gray-900"
                    }`}
                  >
                    Pay Rates
                  </h3>
                  {!isMobile && (
                    <button
                      type="button"
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                      onClick={() => startCreateMode("pay")}
                    >
                      <MdAdd size={16} />
                      Create New
                    </button>
                  )}
                </div>
                <div className="tw-w-full">
                  {!isMobile ? (
                    <div
                      className="ag-theme-balham tw-h-[23dvh]"
                      onContextMenu={handleContextMenu}
                    >
                      <AgGridReact
                        rowData={payRates}
                        columnDefs={rateColumnDefs}
                        rowSelection="single"
                        animateRows
                        onCellContextMenu={handleCellContextMenu}
                        onRowClicked={handlePayRowClicked}
                        getRowStyle={getRowStyle}
                        defaultColDef={{ sortable: true, resizable: true }}
                      />
                      {ContextMenuComponent}
                    </div>
                  ) : (
                    <div className="tw-max-h-[30dvh] tw-overflow-y-auto tw-space-y-2">
                      {payRates.length > 0 ? (
                        payRates.map((rate) => {
                          const isSelected =
                            selectedRate?.id === rate.id &&
                            rateCategory === "pay";
                          return (
                            <button
                              key={rate.id}
                              type="button"
                              className={`tw-w-full tw-text-left tw-border tw-rounded-lg tw-p-3 tw-bg-white tw-transition ${
                                isSelected
                                  ? "tw-border-blue-500 tw-bg-blue-50"
                                  : "tw-border-gray-200 hover:tw-bg-gray-50"
                              }`}
                              onClick={() => {
                                handlePayRowClicked({ data: rate } as any);
                              }}
                            >
                              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                <span
                                  className={`tw-font-medium tw-capitalize ${
                                    !rate.end_date
                                      ? "tw-text-blue-600"
                                      : "tw-text-gray-900"
                                  }`}
                                >
                                  {rate.rate_type}
                                </span>
                                <span
                                  className={`tw-text-sm tw-font-medium ${
                                    !rate.end_date
                                      ? "tw-text-blue-600"
                                      : "tw-text-gray-700"
                                  }`}
                                >
                                  ${Number(rate.value).toFixed(2)}
                                </span>
                              </div>
                              <div
                                className={`tw-text-xs tw-mt-2 tw-space-y-1 ${
                                  !rate.end_date
                                    ? "tw-text-blue-500"
                                    : "tw-text-gray-600"
                                }`}
                              >
                                <div>
                                  <strong>Start:</strong>{" "}
                                  {rate.start_date || "N/A"}
                                </div>
                                <div>
                                  <strong>End:</strong>{" "}
                                  {rate.end_date || "Current"}
                                </div>
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className="tw-text-sm tw-text-gray-500 tw-text-center tw-py-6">
                          No pay rates found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Billing Rates Grid */}
              <div
                className={`tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 ${
                  operationMode && rateCategory === "billing"
                    ? "tw-bg-gray-200"
                    : "tw-bg-white"
                }`}
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                  <h3
                    className={`tw-text-lg tw-font-semibold ${
                      operationMode && rateCategory === "billing"
                        ? "tw-text-blue-700"
                        : "tw-text-gray-900"
                    }`}
                  >
                    Billing Rates
                  </h3>
                  {!isMobile && (
                    <button
                      type="button"
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                      onClick={() => startCreateMode("billing")}
                    >
                      <MdAdd size={16} />
                      Create New
                    </button>
                  )}
                </div>
                <div className="tw-w-full">
                  {!isMobile ? (
                    <div
                      className="ag-theme-balham tw-h-[23dvh]"
                      onContextMenu={handleContextMenu}
                    >
                      <AgGridReact
                        rowData={billingRates}
                        columnDefs={rateColumnDefs}
                        rowSelection="single"
                        animateRows
                        onCellContextMenu={handleCellContextMenu}
                        onRowClicked={handleBillingRowClicked}
                        getRowStyle={getRowStyle}
                        defaultColDef={{ sortable: true, resizable: true }}
                      />
                      {ContextMenuComponent}
                    </div>
                  ) : (
                    <div className="tw-max-h-[30dvh] tw-overflow-y-auto tw-space-y-2">
                      {billingRates.length > 0 ? (
                        billingRates.map((rate) => {
                          const isSelected =
                            selectedRate?.id === rate.id &&
                            rateCategory === "billing";
                          return (
                            <button
                              key={rate.id}
                              type="button"
                              className={`tw-w-full tw-text-left tw-border tw-rounded-lg tw-p-3 tw-bg-white tw-transition ${
                                isSelected
                                  ? "tw-border-blue-500 tw-bg-blue-50"
                                  : "tw-border-gray-200 hover:tw-bg-gray-50"
                              }`}
                              onClick={() => {
                                setSelectedRate(rate);
                                setRateCategory("billing");
                              }}
                            >
                              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                <span
                                  className={`tw-font-medium tw-capitalize ${
                                    !rate.end_date
                                      ? "tw-text-blue-600"
                                      : "tw-text-gray-900"
                                  }`}
                                >
                                  {rate.rate_type}
                                </span>
                                <span
                                  className={`tw-text-sm tw-font-medium ${
                                    !rate.end_date
                                      ? "tw-text-blue-600"
                                      : "tw-text-gray-700"
                                  }`}
                                >
                                  ${Number(rate.value).toFixed(2)}
                                </span>
                              </div>
                              <div
                                className={`tw-text-xs tw-mt-2 tw-space-y-1 ${
                                  !rate.end_date
                                    ? "tw-text-blue-500"
                                    : "tw-text-gray-600"
                                }`}
                              >
                                <div>
                                  <strong>Start:</strong>{" "}
                                  {rate.start_date || "N/A"}
                                </div>
                                <div>
                                  <strong>End:</strong>{" "}
                                  {rate.end_date || "Current"}
                                </div>
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className="tw-text-sm tw-text-gray-500 tw-text-center tw-py-6">
                          No billing rates found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Edit/Create Form */}
            <div
              className={`${!isMobile ? "tw-min-h-[15rem]" : ""} ${
                operationMode && !isMobile
                  ? "tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3"
                  : ""
              }`}
            >
              {error && (
                <div className="tw-p-2 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-text-red-700">
                  {error}
                </div>
              )}
              {success && (
                <div className="tw-p-2 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-text-green-700">
                  {success}
                </div>
              )}
              {operationMode && !isMobile ? (
                <>
                  <h3 className="tw-text-sm tw-font-semibold tw-text-blue-700 tw-mb-2">
                    {operationMode === "edit"
                      ? `Edit ${categoryLabel}`
                      : `Create ${categoryLabel}`}
                  </h3>

                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-min-h-[8rem] md:tw-min-h-[6rem]">
                    {/* Rate Type */}
                    <div>
                      <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-0.5">
                        Rate Type
                      </label>
                      <select
                        className="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded-md tw-text-xs"
                        value={formValues.rate_type}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            rate_type: e.target.value,
                          }))
                        }
                      >
                        {RATE_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Value */}
                    <div>
                      <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-0.5">
                        Value ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded-md tw-text-xs"
                        value={formValues.value}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            value: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Start Date */}
                    <div>
                      <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-0.5">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded-md tw-text-xs"
                        value={formValues.start_date}
                        min={getMinDate()}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            start_date: e.target.value,
                          }))
                        }
                      />
                      <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                        Must be within {RATE_EDIT_WINDOW_DAYS} days of today
                      </p>
                    </div>

                    {/* End Date (only for edit mode, disabled when current rate) */}
                    {operationMode === "edit" && selectedRate && (
                      <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-0.5">
                          End Date
                        </label>
                        <input
                          type="date"
                          className={`tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded-md tw-text-xs ${
                            !selectedRate.end_date
                              ? "tw-bg-gray-100 tw-text-gray-500 tw-cursor-not-allowed"
                              : ""
                          }`}
                          value={formValues.end_date || ""}
                          disabled={!selectedRate.end_date}
                          onChange={(e) =>
                            setFormValues((prev) => ({
                              ...prev,
                              end_date: e.target.value,
                            }))
                          }
                        />
                        {!selectedRate.end_date && (
                          <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                            Current rate — end date cannot be changed
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-200">
                    <button
                      type="button"
                      onClick={() => clearOperation()}
                      className="tw-px-2 tw-py-1 tw-text-xs tw-text-gray-600 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                    >
                      {operationMode === "edit" ? "Cancel Edit" : "Cancel"}
                    </button>
                    <HeadlessButton
                      type="button"
                      disabled={isSaving}
                      onClick={handleSave}
                      className="md:tw-w-[30%] tw-h-8 tw-text-xs"
                    >
                      {isSaving ? (
                        <Spinner className="tw-flex tw-justify-center tw-items-center" />
                      ) : operationMode === "edit" ? (
                        `Update ${categoryLabel}`
                      ) : (
                        `Create ${categoryLabel}`
                      )}
                    </HeadlessButton>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </ScrollableModalBody>
      )}
    </HeadlessDialog>
  );
}
